import { FormGroup, Icon } from "@blueprintjs/core";
import React, { useCallback, useMemo, forwardRef } from "react";
import {
  ActionCompleteEventArgs,
  RemovingEventArgs,
  UploaderComponent,
  UploaderModel,
  UploadingEventArgs,
} from "@syncfusion/ej2-react-inputs";
import {
  FieldLabeledProps,
  FileFieldValue,
  FileProps,
} from "../../types/fieldTypes";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { IconNames } from "@blueprintjs/icons";
import { useFileField } from "./useFileField";
import { FileDocument } from "./types";

/** Base FileField Props*/
export interface FileFieldProps extends FileProps {
  uploaderConfig: Omit<
    UploaderModel,
    "enabled" | "success" | "uploading" | "removing"
  >;
  onUploading?: (args: UploadingEventArgs) => void;
  onChunkUploading?: (args: UploadingEventArgs) => void;
  onRemoving?: (args: RemovingEventArgs) => void;
  onCompleteUploading?: (doc: FileDocument) => void;
  onCompleteRemoving?: (doc?: FileDocument) => void;
  onAllActionComplete?: (args: ActionCompleteEventArgs) => void;
  getAuthRequestHeader?: () => Promise<Record<string, string> | undefined>;
  showPreview?: boolean;
  handleOnPreview?: () => void;
  documentsFolderName?: string;
  labelProps?: FieldLabeledProps;
  documentId?: string;
  onCustomFormData?: () => any;
  className?: string;
  skipErrorMessagesHandling?: boolean;
}

// Custom unique ID generator
const generateUniqueId = () => {
  const genId = `documentFile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return genId;
};

const FileField = forwardRef<UploaderComponent, FileFieldProps>(
  (props, ref) => {
    const fieldProps = useMemo(() => props, [props]);

    const FileFieldComponent = useMemo(() => React.memo(FileFieldInner), []);

    const handleSubmit = useCallback((value: FileFieldValue) => {
      props.onSubmit && props.onSubmit(value);
    }, []);

    const handleUploading = useCallback((args: UploadingEventArgs) => {
      props.onUploading && props.onUploading(args);
    }, []);

    const handleChunkUploading = useCallback((args: UploadingEventArgs) => {
      props.onChunkUploading && props.onChunkUploading(args);
    }, []);

    const handleRemoving = useCallback((args: RemovingEventArgs) => {
      props.onRemoving && props.onRemoving(args);
    }, []);

    const handleCompleteUploading = useCallback((doc: FileDocument) => {
      props.onCompleteUploading && props.onCompleteUploading(doc);
    }, []);

    const handleCompleteRemoving = useCallback((doc?: FileDocument) => {
      props.onCompleteRemoving && props.onCompleteRemoving(doc);
    }, []);

    const handleAllActionComplete = useCallback(
      (args: ActionCompleteEventArgs) => {
        props.onAllActionComplete && props.onAllActionComplete(args);
      },
      []
    );

    const handleGetAuthRequestHeader = useCallback(async () => {
      return props.getAuthRequestHeader
        ? await props.getAuthRequestHeader()
        : undefined;
    }, [props.getAuthRequestHeader]);

    const handleOnPreview = useCallback(() => {
      props.handleOnPreview && props.handleOnPreview();
    }, []);

    return (
      <FileFieldComponent
        {...fieldProps}
        ref={ref}
        onSubmit={handleSubmit}
        onUploading={handleUploading}
        onChunkUploading={handleChunkUploading}
        onRemoving={handleRemoving}
        onCompleteUploading={handleCompleteUploading}
        onCompleteRemoving={handleCompleteRemoving}
        onAllActionComplete={handleAllActionComplete}
        getAuthRequestHeader={handleGetAuthRequestHeader}
        handleOnPreview={handleOnPreview}
      />
    );
  }
) as React.ForwardRefExoticComponent<
  FileFieldProps & React.RefAttributes<UploaderComponent>
>;

FileField.displayName = "FileField";

const FileFieldInner = forwardRef<UploaderComponent, FileFieldProps>(
  (props, ref) => {
    const { fileFieldRef, resolvedUploaderConfig } = useFileField(props);

    // Use the forwarded ref if provided, otherwise use the internal ref
    const uploaderRef = ref || fileFieldRef;

    return (
      <div className={`ucl-base-input-file-field-container ${props.className}`}>
        <FormGroup
          className={`${
            props.isRequired && !props.value
              ? "has-required-background"
              : "base-field"
          }`}
        >
          {props.label && (
            <FieldLabel
              labelName={props.label}
              tooltipDescription={props.description}
              fieldProps={{ ...props }}
              isRequired={props.isRequired}
              {...props.labelProps}
            />
          )}
          <section className="ucl-field-header file">
            {props.showPreview && (
              <span className="ucl-file-field preview">
                {props.value && (
                  <Icon
                    title="Preview"
                    icon={IconNames.EyeOpen}
                    onClick={props.handleOnPreview}
                  />
                )}
              </span>
            )}
          </section>
          <UploaderComponent
            ref={uploaderRef}
            {...resolvedUploaderConfig}
            id={generateUniqueId()}
            type="file"
          />
          {props.errorMessages && !props.skipErrorMessagesHandling && (
            <FormFieldErrorContainer errorMessages={props.errorMessages} />
          )}
        </FormGroup>
      </div>
    );
  }
) as React.ForwardRefExoticComponent<
  FileFieldProps & React.RefAttributes<UploaderComponent>
>;

FileFieldInner.displayName = "FileFieldInner";

export default FileField;
